import { useState } from "react";
import { MessageCircle, MapPin, LocateFixed, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { BOOKABLE_SERVICES, buildBookingMessage, openWhatsApp } from "@/lib/constants";

const EMPTY = { name: "", phone: "", address: "", mapLink: "", date: "", time: "", services: [] };

const inputCls =
  "h-12 rounded-none border-[#B08D2E]/20 bg-[#F9F6F0]/60 px-4 text-[#1A1A1A] placeholder:text-[#6E675C]/50 focus-visible:ring-1 focus-visible:ring-[#B08D2E] focus-visible:ring-offset-0 [color-scheme:dark]";

const labelCls =
  "mb-2 block text-[10px] font-medium uppercase tracking-[0.3em] text-[#6E675C]";

export default function BookingModal({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [showMapLink, setShowMapLink] = useState(false);
  const [locating, setLocating] = useState(false);

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support location detection — please paste a Maps link instead.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setForm((f) => ({
          ...f,
          mapLink: `https://maps.google.com/?q=${latitude.toFixed(6)},${longitude.toFixed(6)}`,
        }));
        setShowMapLink(true);
        setLocating(false);
        toast.success("Location detected — your map pin is added to the booking.");
      },
      () => {
        setLocating(false);
        toast.error("Couldn't detect your location. Please allow location access, or paste a Maps link instead.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const detectButton = (
    <button
      type="button"
      data-testid="booking-detect-location-btn"
      onClick={detectLocation}
      disabled={locating}
      className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-[#B08D2E] transition-colors duration-300 hover:text-[#8C6D1F] disabled:opacity-50"
    >
      {locating ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
      ) : (
        <LocateFixed className="h-3.5 w-3.5" strokeWidth={1.5} />
      )}
      {locating ? "Detecting…" : "Detect My Current Location"}
    </button>
  );

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleService = (service) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(service)
        ? f.services.filter((s) => s !== service)
        : [...f.services, service],
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.address.trim() || !form.date || !form.time) {
      toast.error("Please complete every field before sending.");
      return;
    }
    if (form.services.length === 0) {
      toast.error("Please select at least one service.");
      return;
    }
    openWhatsApp(buildBookingMessage(form));
    toast.success("Opening WhatsApp with your booking details…");
    onOpenChange(false);
    setForm(EMPTY);
    setShowMapLink(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-modal"
        className="max-h-[92vh] w-[calc(100vw-1.5rem)] overflow-y-auto border-[#B08D2E]/25 bg-[#FFFFFF] p-5 text-[#1A1A1A] sm:w-full sm:max-w-xl sm:p-6"
      >
        <DialogHeader>
          <p className="text-[10px] font-medium uppercase tracking-[0.4em] text-[#B08D2E]">
            WhatsApp Booking
          </p>
          <DialogTitle className="font-display text-3xl font-light text-[#1A1A1A]">
            Book a <em className="gold-text font-medium italic">Pickup</em>
          </DialogTitle>
          <DialogDescription className="text-sm font-light text-[#6E675C]">
            Fill in your details below — we will open WhatsApp with everything
            written and ready to send.
          </DialogDescription>
        </DialogHeader>

        <div
          data-testid="delivery-policy-note"
          className="border border-[#B08D2E]/25 bg-[#FBF8F0] px-4 py-3.5"
        >
          <p className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.25em] text-[#8C6D1F]">
            <Truck className="h-3.5 w-3.5" strokeWidth={1.5} />
            Delivery Policy
          </p>
          <ul className="mt-2.5 space-y-1.5 text-[11.5px] font-light leading-relaxed text-[#6E675C]">
            <li className="flex items-start gap-2.5">
              <span className="mt-[5px] h-1 w-1 shrink-0 rotate-45 bg-[#B08D2E]" />
              <span>Up to 5 miles: Free pickup &amp; delivery on orders £50+ (charges apply for lower amounts).</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="mt-[5px] h-1 w-1 shrink-0 rotate-45 bg-[#B08D2E]" />
              <span>5–10 miles: Free pickup &amp; delivery on orders £80+ (charges apply for lower amounts).</span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="mt-[5px] h-1 w-1 shrink-0 rotate-45 bg-[#B08D2E]" />
              <span>Beyond 10 miles: Standard delivery charges apply to all orders.</span>
            </li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="mt-2 space-y-6">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="booking-name" className={labelCls}>
                Name
              </label>
              <Input
                id="booking-name"
                data-testid="booking-name-input"
                value={form.name}
                onChange={set("name")}
                placeholder="Your full name"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="booking-phone" className={labelCls}>
                Phone Number
              </label>
              <Input
                id="booking-phone"
                data-testid="booking-phone-input"
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="e.g. 07123 456789"
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label htmlFor="booking-address" className={labelCls}>
              Pickup Address
            </label>
            <Textarea
              id="booking-address"
              data-testid="booking-address-input"
              value={form.address}
              onChange={set("address")}
              placeholder="Street, town and postcode"
              className={`${inputCls} min-h-[76px] py-3`}
            />
            {showMapLink ? (
              <div className="mt-3">
                <label htmlFor="booking-map-link" className={labelCls}>
                  Google Maps Location / Link{" "}
                  <span className="normal-case tracking-normal text-[#6E675C]/70">
                    (optional)
                  </span>
                </label>
                <Input
                  id="booking-map-link"
                  data-testid="booking-map-link-input"
                  value={form.mapLink}
                  onChange={set("mapLink")}
                  placeholder="Paste a Google Maps link or coordinates"
                  className={inputCls}
                />
                <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                  <p className="text-[11px] font-light leading-relaxed text-[#6E675C]/80">
                    Paste a link from Google Maps, or let us find you.
                  </p>
                  {detectButton}
                </div>
              </div>
            ) : (
              <div className="mt-3 flex flex-wrap items-center gap-x-7 gap-y-2.5">
                <button
                  type="button"
                  data-testid="booking-add-map-link-btn"
                  onClick={() => setShowMapLink(true)}
                  className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-[#B08D2E] transition-colors duration-300 hover:text-[#8C6D1F]"
                >
                  <MapPin className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Or Share Google Maps Location / Link
                </button>
                {detectButton}
              </div>
            )}
          </div>

          <div>
            <span className={labelCls}>Selected Services</span>
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2" data-testid="booking-services-group">
              {BOOKABLE_SERVICES.map((service) => {
                const slug = service.toLowerCase().replace(/[^a-z0-9]+/g, "-");
                const checked = form.services.includes(service);
                return (
                  <label
                    key={service}
                    className={`flex cursor-pointer items-center gap-3 border px-3.5 py-3 text-xs font-light tracking-wide transition-[border-color,background-color] duration-300 ${
                      checked
                        ? "border-[#B08D2E]/70 bg-[#B08D2E]/10 text-[#1A1A1A]"
                        : "border-[#B08D2E]/15 text-[#6E675C] hover:border-[#B08D2E]/40"
                    }`}
                  >
                    <Checkbox
                      data-testid={`booking-service-${slug}`}
                      checked={checked}
                      onCheckedChange={() => toggleService(service)}
                      className="rounded-none border-[#B08D2E]/40 data-[state=checked]:border-[#B08D2E] data-[state=checked]:bg-[#B08D2E] data-[state=checked]:text-[#F9F6F0]"
                    />
                    {service}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label htmlFor="booking-date" className={labelCls}>
                Preferred Date
              </label>
              <Input
                id="booking-date"
                data-testid="booking-date-input"
                type="date"
                min={new Date().toISOString().split("T")[0]}
                value={form.date}
                onChange={set("date")}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="booking-time" className={labelCls}>
                Preferred Time
              </label>
              <Input
                id="booking-time"
                data-testid="booking-time-input"
                type="time"
                value={form.time}
                onChange={set("time")}
                className={inputCls}
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="booking-submit-button"
            className="group relative flex w-full items-center justify-center gap-3 overflow-hidden bg-[#B08D2E] py-4 text-xs font-semibold uppercase tracking-[0.28em] text-[#F9F6F0] transition-colors duration-300 hover:bg-[#96771F]"
          >
            <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent transition-transform duration-700 ease-out group-hover:translate-x-full" />
            <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
            Send Booking via WhatsApp
          </button>
          <p className="pb-1 text-center text-[11px] font-light tracking-wide text-[#6E675C]/70">
            Your booking opens in WhatsApp to +44 7568 487789 — nothing is stored
            on this site.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
